<template>
  <div class="page">
    <ActionBar :breadcrumbItems="breadcrumbItems">
      <MenuItemPeriod
        v-if="
          hasRole('report', 'ROLE_GET_REPORT_COSTCENTER') &&
          hasRole('report', 'ROLE_GET_REPORT_TAXIJOBS') &&
          hasRole('report', 'ROLE_GET_REPORT_CHART') &&
          hasRole('report', 'ROLE_GET_REPORT_EMPLOYEE')
        "
        icon
        @change="updateFilters"
      />
    </ActionBar>

    <div class="page--center-content">
      <div>
        <Performance v-if="hasRole('report', 'ROLE_GET_REPORT_CHART')" :filters="filters" />
      </div>

      <v-layout wrap>
        <v-flex v-if="!sidebarVisible && hasRole('report', 'ROLE_GET_REPORT_TAXIJOBS')" lg4 xs12>
          <LatestRides
            :min-height="boxHeight"
            :filters="filters"
            :route="{ name: 'Reports', params: { type: 'corridas' } }"
            @getRideData="openRideDrawer"
          />
        </v-flex>

        <v-flex lg6 xs12>
          <Top5
            v-if="hasRole('report', 'ROLE_GET_REPORT_COSTCENTER')"
            :min-height="boxHeight"
            :route="{
              name: 'Reports',
              params: { type: 'centro-de-custo' }
            }"
            type="costCenters"
            :items="top5CostCenter.items"
            :status="top5CostCenter.status"
            class="mr2"
            @tryAgain="getCostCenters"
            @openGlobalDrawer="openCostCenterDrawer"
          />
        </v-flex>

        <v-flex lg6 xs12>
          <Top5
            v-if="hasRole('report', 'ROLE_GET_REPORT_EMPLOYEE')"
            :min-height="boxHeight"
            :route="{
              name: 'Reports',
              params: { type: 'colaborador' }
            }"
            type="employees"
            :items="top5Employee.items"
            :status="top5Employee.status"
            class="ml2"
            @tryAgain="getEmployee"
            @openGlobalDrawer="openEmployeeDrawer"
          />
        </v-flex>
      </v-layout>
    </div>

    <LatestRidesSidebar
      v-if="sidebarVisible"
      :filters="filters"
      @openRideDrawer="openRideDrawer"
    />

    <GlobalDrawer
      :show="globalDrawer.visibility"
      :drawerType="globalDrawer.type"
      :data="globalDrawer.data"
      :filters="globalDrawer.filters"
      @close="closeGlobalDrawer"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { hasRole, convertToCurrency } from 'utils/functions'
import { isValidObject, catchError } from 'corp-front-vue-commons/src/utils/functions'
import globalDrawerMixin from 'mixins/globalDrawer'
import ActionBar from 'components/Actionbar'
import MenuItemPeriod from 'components/MenuItemPeriod'
import Performance from 'components/pages/dashboard/performance'
import LatestRides from 'components/pages/dashboard/latestRides'
import Top5 from 'components/pages/dashboard/top5'
import LatestRidesSidebar from 'components/pages/dashboard/LatestRidesSidebar'
import GlobalDrawer from 'components/GlobalDrawer'

export default {
  name: 'DashboardPage',
  mixins: [globalDrawerMixin],
  components: {
    ActionBar,
    MenuItemPeriod,
    Performance,
    LatestRides,
    Top5,
    LatestRidesSidebar,
    GlobalDrawer
  },
  computed: {
    ...mapState({
      selectedCompany: ({ company }) => company.selectedCompany
    })
  },
  data () {
    return {
      breadcrumbItems: [
        { name: 'Dashboard', title: this.$t('Views.DashboardPage.title') }
      ],
      filters: {
        startDate: null,
        endDate: null
      },
      boxHeight: 260,
      top5CostCenter: {
        status: null,
        items: []
      },
      top5Employee: {
        status: null,
        items: []
      },
      sidebarVisible: false
    }
  },
  methods: {
    hasRole (group, role) {
      return hasRole(group, role)
    },

    isValidObject (val) {
      return isValidObject(val)
    },

    getFilters () {
      return this.isValidObject(this.filters) && this.filters.startDate && this.filters.endDate
        ? { ...this.filters }
        : {}
    },

    openRideDrawer (ride) {
      this.openGlobalDrawer({
        visibility: true,
        type: 'ride',
        data: { id: ride.taxiJobId },
        filters: { ...this.getFilters() }
      })
    },

    openCostCenterDrawer (costCenter) {
      this.openGlobalDrawer({
        visibility: true,
        ...costCenter,
        filters: {
          ...this.getFilters(),
          costCenterId: costCenter.id
        }
      })
    },

    openEmployeeDrawer (employee) {
      this.openGlobalDrawer({
        visibility: true,
        ...employee,
        filters: {
          ...this.getFilters(),
          employeeId: employee.employeeId
        }
      })
    },

    updateFilters ({ startDate, endDate }) {
      this.filters = { startDate, endDate }
    },

    async getCostCenters () {
      if (this.top5CostCenter.status === 'loading') return

      this.top5CostCenter.status = 'loading'

      try {
        const startDate = this.filters.startDate || null
        const endDate = this.filters.endDate || null
        const companyId = this.selectedCompany ? this.selectedCompany.id : null
        const { data: res } = await this.$apiService.reports.getCostCenters(
          { orderBy: 'totalFare' },
          { page: 1 },
          { limit: 5 },
          { startDate },
          { endDate },
          { companyId }
        )
        const { series, total } = res.data

        this.top5CostCenter.items = series.map(item => {
          return {
            name: item.name,
            value: convertToCurrency(item.totalFare),
            percent: (item.totalFare / total.totalFare) * 100,
            info: item
          }
        })

        this.top5CostCenter.status = 'success'
      } catch (error) {
        this.top5CostCenter.status = 'error'
        catchError(error)
      }
    },

    async getEmployee () {
      if (this.top5Employee.status === 'loading') return

      this.top5Employee.status = 'loading'

      try {
        const startDate = this.filters.startDate || null
        const endDate = this.filters.endDate || null
        const companyId = this.selectedCompany ? this.selectedCompany.id : null
        const { data: res } = await this.$apiService.reports.getEmployee(
          { orderBy: 'totalFare' },
          { page: 1 },
          { limit: 5 },
          { startDate },
          { endDate },
          { companyId }
        )

        if (res) {
          const { series, total } = res.data

          this.top5Employee.items = series.map(item => {
            return {
              name: item.employeeName,
              value: convertToCurrency(item.totalFare),
              percent: (item.totalFare / total.totalFare) * 100,
              info: item
            }
          })
        }

        this.top5Employee.status = 'success'
      } catch (error) {
        this.top5Employee.status = 'error'
        catchError(error)
      }
    },

    handleSidebarVisible () {
      this.sidebarVisible = this.hasRole('report', 'ROLE_GET_REPORT_TAXIJOBS') && window.innerWidth >= 1140
    }
  },
  async mounted () {
    this.handleSidebarVisible()

    await Promise.all([
      this.getCostCenters(),
      this.getEmployee()
    ])

    window.addEventListener('resize', this.handleSidebarVisible)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.handleSidebarVisible)
  },
  watch: {
    filters: {
      deep: true,
      async handler () {
        await Promise.all([
          this.getCostCenters(),
          this.getEmployee()
        ])

        this.closeGlobalDrawer()
      }
    },

    async selectedCompany () {
      await Promise.all([
        this.getCostCenters(),
        this.getEmployee()
      ])

      this.closeGlobalDrawer()
    }
  }
}
</script>

<style lang="scss" >
.route--dashboard {

  .ml2 { margin-left: 0; }
  .mr2 { margin-right: 0; }

  @media (min-width: 1140px) {
    padding-right: 360px;
  }

  @media (min-width: 1264px) {
    .ml2 { margin-left: 8px; }
    .mr2 { margin-right: 8px; }
  }
}
</style>
